import ExprienceProjectPage from '@/pages/ProjectPage/ProjectPage'
import ExperienceProjectPage from '@/pages/ExperienceProjectPage/ExperienceProjectPage'
import {
  HORIZONTAL_BASE_PREFIX,
  HORIZONTAL_QUICK_PREFIX,
  HORIZONTAL_SURVEY_PREFIX,
  PAGE_LINKS,
  PORTRAIT_BASE_PREFIX,
  PORTRAIT_QUICK_PREFIX,
  PORTRAIT_SURVEY_PREFIX,
} from '../pageConst'
import ProjectCreatePage from '@/pages/ProjectCreatePage/ProjectCreatePage'
import SettingPage from '@/pages/SettingPage/SettingPage'
import AccountPage from '@/pages/AccountPage/AccountPage'
import SubScriptionPage from '@/pages/SubscriptionPage/SubScriptionPage'
import { Navigate, RouteObject } from 'react-router-dom'
import AuthRenderer from '@/components/AuthRenderer/AuthRenderer'
import StatisticPage from '@/pages/StatisticPage/StatisticPage'
import PortraitStatisticPage from '@/pages/PortraitStatisticPage/PortraitStatisticPage'
import StatisticComparePage from '@/pages/StatisticComparePage/StatisticComparePage'
import StatisticCompareStartPage from '@/pages/StatisticCompareStartPage/StatisticCompareStartPage'
import StatisticCompareInfoPage from '@/pages/StatisticCompareInfoPage/StatisticCompareInfoPage'
import PersistentDashboardLayout from '@/components/PersistentDashboardLayout/PersistentDashboardLayout'
import ComingSoonPage from '@/pages/ComingSoonPage/ComingSoonPage'
import PortraitSurveyProjectPage from '@/pages/PortraitSurveyProjectPage/PortraitSurveyProjectPage'

const wrapWithAuthRenderer = (routes: RouteObject[]) =>
  routes.map((route) => {
    if (route.index || route.path === '' || route.path === '*' || !route.element) {
      return route
    }
    return {
      ...route,
      element: <AuthRenderer>{route.element}</AuthRenderer>,
    }
  })

const createDashboardChildRoutes = (
  links: {
    project: string
    projectCreate: string
    projectDetail: string
    setting: string
    account: string
    subscription: string
    statistic: string
    statisticCompare: string
    statisticCompareStart: string
    statisticCompareInfo: string
  },
  projectPage: JSX.Element,
  statisticPage: JSX.Element = <StatisticPage />
): RouteObject[] => [
  {
    path: '',
    element: <Navigate to={links.project} replace />,
  },
  {
    path: links.project,
    element: projectPage,
  },
  {
    path: links.projectCreate,
    element: <ProjectCreatePage />,
  },
  {
    path: links.projectDetail,
    element: projectPage,
  },
  {
    path: links.setting,
    element: <SettingPage />,
  },
  {
    path: links.account,
    element: <AccountPage />,
  },
  {
    path: links.subscription,
    element: <SubScriptionPage />,
  },
  {
    path: getStatisticRootPath(links.statistic),
    element: <Navigate to={links.statistic} replace />,
  },
  {
    path: links.statistic,
    element: statisticPage,
  },
  {
    path: links.statisticCompare,
    element: <StatisticComparePage />,
  },
  {
    path: links.statisticCompareStart,
    element: <StatisticCompareStartPage />,
  },
  {
    path: links.statisticCompareInfo,
    element: <StatisticCompareInfoPage />,
  },
  {
    path: '*',
    element: <Navigate to={links.project} replace />,
  },
]

const horizontalBaseChildRoutes = createDashboardChildRoutes(
  {
    project: PAGE_LINKS.project,
    projectCreate: PAGE_LINKS.projectCreate,
    projectDetail: PAGE_LINKS.projectDetail,
    setting: PAGE_LINKS.setting,
    account: PAGE_LINKS.account,
    subscription: PAGE_LINKS.subscription,
    statistic: PAGE_LINKS.statistic,
    statisticCompare: PAGE_LINKS.statisticCompare,
    statisticCompareStart: PAGE_LINKS.statisticCompareStart,
    statisticCompareInfo: PAGE_LINKS.statisticCompareInfo,
  },
  <ExprienceProjectPage />
)

const horizontalQuickChildRoutes = createDashboardChildRoutes(
  {
    project: PAGE_LINKS.quickProject,
    projectCreate: PAGE_LINKS.quickProjectCreate,
    projectDetail: PAGE_LINKS.quickProjectDetail,
    setting: PAGE_LINKS.quickSetting,
    account: PAGE_LINKS.quickAccount,
    subscription: PAGE_LINKS.quickSubscription,
    statistic: PAGE_LINKS.quickStatistic,
    statisticCompare: PAGE_LINKS.quickStatisticCompare,
    statisticCompareStart: PAGE_LINKS.quickStatisticCompareStart,
    statisticCompareInfo: PAGE_LINKS.quickStatisticCompareInfo,
  },
  <ExperienceProjectPage />
)

const horizontalSurveyChildRoutes = createDashboardChildRoutes(
  {
    project: PAGE_LINKS.experienceProject,
    projectCreate: PAGE_LINKS.experienceProjectCreate,
    projectDetail: PAGE_LINKS.experienceProjectDetail,
    setting: PAGE_LINKS.experienceSetting,
    account: PAGE_LINKS.experienceAccount,
    subscription: PAGE_LINKS.experienceSubscription,
    statistic: PAGE_LINKS.experienceStatistic,
    statisticCompare: PAGE_LINKS.experienceStatisticCompare,
    statisticCompareStart: PAGE_LINKS.experienceStatisticCompareStart,
    statisticCompareInfo: PAGE_LINKS.experienceStatisticCompareInfo,
  },
  <ExperienceProjectPage />
)

const portraitQuickChildRoutes = createDashboardChildRoutes(
  {
    project: PAGE_LINKS.portraitQuickProject,
    projectCreate: PAGE_LINKS.portraitQuickProjectCreate,
    projectDetail: PAGE_LINKS.portraitQuickProjectDetail,
    setting: PAGE_LINKS.portraitQuickSetting,
    account: PAGE_LINKS.portraitQuickAccount,
    subscription: PAGE_LINKS.portraitQuickSubscription,
    statistic: PAGE_LINKS.portraitQuickStatistic,
    statisticCompare: PAGE_LINKS.portraitQuickStatisticCompare,
    statisticCompareStart: PAGE_LINKS.portraitQuickStatisticCompareStart,
    statisticCompareInfo: PAGE_LINKS.portraitQuickStatisticCompareInfo,
  },
  <ExperienceProjectPage />,
  <PortraitStatisticPage />
)

const portraitBaseChildRoutes = createDashboardChildRoutes(
  {
    project: PAGE_LINKS.portraitProject,
    projectCreate: PAGE_LINKS.portraitProjectCreate,
    projectDetail: PAGE_LINKS.portraitProjectDetail,
    setting: PAGE_LINKS.portraitSetting,
    account: PAGE_LINKS.portraitAccount,
    subscription: PAGE_LINKS.portraitSubscription,
    statistic: PAGE_LINKS.portraitStatistic,
    statisticCompare: PAGE_LINKS.portraitStatisticCompare,
    statisticCompareStart: PAGE_LINKS.portraitStatisticCompareStart,
    statisticCompareInfo: PAGE_LINKS.portraitStatisticCompareInfo,
  },
  <ExprienceProjectPage />,
  <PortraitStatisticPage />
)

const portraitSurveyChildRoutes: RouteObject[] = [
  {
    path: '',
    element: <Navigate to={PAGE_LINKS.portraitExperienceProject} replace />,
  },
  {
    path: PAGE_LINKS.portraitExperienceProject,
    element: <PortraitSurveyProjectPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceProjectCreate,
    element: <ProjectCreatePage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceProjectDetail,
    element: <PortraitSurveyProjectPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceSetting,
    element: <ComingSoonPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceAccount,
    element: <AccountPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceSubscription,
    element: <SubScriptionPage />,
  },
  {
    path: getStatisticRootPath(PAGE_LINKS.portraitExperienceStatistic),
    element: <Navigate to={PAGE_LINKS.portraitExperienceStatistic} replace />,
  },
  {
    path: PAGE_LINKS.portraitExperienceStatistic,
    element: <PortraitStatisticPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceStatisticCompare,
    element: <StatisticComparePage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceStatisticCompareStart,
    element: <StatisticCompareStartPage />,
  },
  {
    path: PAGE_LINKS.portraitExperienceStatisticCompareInfo,
    element: <StatisticCompareInfoPage />,
  },
  {
    path: '*',
    element: <Navigate to={PAGE_LINKS.portraitExperienceProject} replace />,
  },
]

function getStatisticRootPath(statisticOverviewPath: string) {
  return statisticOverviewPath.replace(/\/overview$/, '')
}

export const dashboardRoutes: RouteObject[] = [
  {
    path: '/',
    element: <PersistentDashboardLayout />,
    children: [
      {
        path: HORIZONTAL_QUICK_PREFIX,
        children: wrapWithAuthRenderer(horizontalQuickChildRoutes),
      },
      {
        path: HORIZONTAL_BASE_PREFIX,
        children: wrapWithAuthRenderer(horizontalBaseChildRoutes),
      },
      {
        path: HORIZONTAL_SURVEY_PREFIX,
        children: wrapWithAuthRenderer(horizontalSurveyChildRoutes),
      },
      {
        path: PORTRAIT_QUICK_PREFIX,
        children: wrapWithAuthRenderer(portraitQuickChildRoutes),
      },
      {
        path: PORTRAIT_BASE_PREFIX,
        children: wrapWithAuthRenderer(portraitBaseChildRoutes),
      },
      {
        path: PORTRAIT_SURVEY_PREFIX,
        children: wrapWithAuthRenderer(portraitSurveyChildRoutes),
      },
    ],
  },
]

export const experienceDashboardRoutes: RouteObject[] = []
